import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
import { auth, db } from './FirebaseConfig'
import {
  signInAnonymously,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut as firebaseSignOut,
} from 'firebase/auth'
import { onDisconnect, ref, set, get, update } from 'firebase/database'

const AuthContext = createContext()

export const useAuthFromContext = () => useContext(AuthContext)

// Set when the player taps "Play as guest". Firebase persists the pre-warmed
// anonymous user whether or not it was used, so without this a restored guest
// would skip the sign-in screen on the next load.
const GUEST_CHOSEN_KEY = 'hogGuestChosen'
const guestChosen = () => {
  try { return localStorage.getItem(GUEST_CHOSEN_KEY) === '1' } catch { return false }
}
const setGuestChosen = (chosen) => {
  try {
    if (chosen) localStorage.setItem(GUEST_CHOSEN_KEY, '1')
    else localStorage.removeItem(GUEST_CHOSEN_KEY)
  } catch {}
}

const ACTION_CODE_SETTINGS = {
  url: window.location.origin + '/',
  handleCodeInApp: true,
}

async function writeUserRecord(firebaseUser) {
  const userRef = ref(db, `users/${firebaseUser.uid}`)
  const snapshot = await get(userRef)
  const now = Date.now()

  if (!snapshot.exists()) {
    const displayName = firebaseUser.isAnonymous
      ? 'Guest'
      : (firebaseUser.email?.split('@')[0] ?? 'Player')
    await set(userRef, {
      id: firebaseUser.uid,
      email: firebaseUser.email ?? null,
      displayName,
      isAnonymous: firebaseUser.isAnonymous,
      createdAt: now,
      lastSignInAt: now,
    })
    return displayName
  } else {
    await update(userRef, { lastSignInAt: now })
    return snapshot.val().displayName
  }
}

export const AuthProvider = ({ children }) => {
  // 'loading' | 'unauthenticated' | 'authenticated' | 'anonymous'
  const [authStatus, setAuthStatus] = useState('loading')
  const [user, setUser] = useState(null)
  const [userRecord, setUserRecord] = useState(null)
  const [needsEmailForLink, setNeedsEmailForLink] = useState(false)

  // Pre-warm state: null | 'pending' | FirebaseUser
  // When the sign-in screen appears we kick off signInAnonymously immediately so
  // clicking "Play as guest" can commit instantly instead of waiting for the network.
  const preWarmRef = useRef(null)
  const preWarmPromiseRef = useRef(null)

  function commitAuth(firebaseUser) {
    const optimisticName = firebaseUser.isAnonymous
      ? 'Guest'
      : (firebaseUser.email?.split('@')[0] ?? 'Player')
    setUser(firebaseUser)
    setUserRecord({ displayName: optimisticName, email: firebaseUser.email })
    setAuthStatus(firebaseUser.isAnonymous ? 'anonymous' : 'authenticated')
    setNeedsEmailForLink(false)

    writeUserRecord(firebaseUser).then(displayName => {
      setUserRecord({ displayName, email: firebaseUser.email })
    })

    const playerRef = ref(db, `players/${firebaseUser.uid}`)
    onDisconnect(playerRef).remove()
    get(playerRef).then(snap => {
      if (!snap.exists()) set(playerRef, { id: firebaseUser.uid })
    })
  }

  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      const savedEmail = localStorage.getItem('emailForSignIn')
      if (savedEmail) {
        signInWithEmailLink(auth, savedEmail, window.location.href)
          .then(() => {
            localStorage.removeItem('emailForSignIn')
            window.history.replaceState({}, document.title, window.location.pathname)
          })
          .catch(console.error)
      } else {
        setNeedsEmailForLink(true)
      }
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        if (firebaseUser.isAnonymous && (preWarmRef.current === 'pending' || !guestChosen())) {
          // Pre-warm completed, or a persisted pre-warm the player never chose —
          // hold the user but don't show the lobby yet. commitAuth will be
          // called explicitly when the user clicks "Play as guest".
          preWarmRef.current = firebaseUser
          preWarmPromiseRef.current = null
          setAuthStatus('unauthenticated')
        } else {
          preWarmRef.current = null
          preWarmPromiseRef.current = null
          commitAuth(firebaseUser)
        }
      } else {
        preWarmRef.current = null
        preWarmPromiseRef.current = null
        setUser(null)
        setUserRecord(null)
        setAuthStatus('unauthenticated')

        // Kick off anonymous auth immediately so the credential is ready by the
        // time the user clicks "Play as guest" — the network round-trip happens
        // while they're reading the screen.
        preWarmRef.current = 'pending'
        const promise = signInAnonymously(auth)
        preWarmPromiseRef.current = promise
        promise.catch(() => {
          if (preWarmRef.current === 'pending') {
            preWarmRef.current = null
            preWarmPromiseRef.current = null
          }
        })
      }
    })

    return () => unsubscribe()
  }, [])

  async function sendMagicLink(email) {
    try {
      await sendSignInLinkToEmail(auth, email, ACTION_CODE_SETTINGS)
      localStorage.setItem('emailForSignIn', email)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  async function completeLinkSignIn(email) {
    try {
      await signInWithEmailLink(auth, email, window.location.href)
      localStorage.removeItem('emailForSignIn')
      window.history.replaceState({}, document.title, window.location.pathname)
      setNeedsEmailForLink(false)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  async function signInAsGuest() {
    const preWarm = preWarmRef.current
    setGuestChosen(true)

    if (preWarm && preWarm !== 'pending') {
      // Pre-warm finished — commit instantly, no network call needed.
      preWarmRef.current = null
      preWarmPromiseRef.current = null
      commitAuth(preWarm)
    } else if (preWarmPromiseRef.current) {
      // Pre-warm still in flight — clear the hold flag so onAuthStateChanged
      // will commit when it fires, then wait for the same promise.
      preWarmRef.current = null
      await preWarmPromiseRef.current
    } else {
      await signInAnonymously(auth)
    }
  }

  async function signOut() {
    setGuestChosen(false)
    await firebaseSignOut(auth)
  }

  return (
    <AuthContext.Provider value={{
      user,
      userRecord,
      authStatus,
      needsEmailForLink,
      sendMagicLink,
      completeLinkSignIn,
      signInAsGuest,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  )
}
